import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { LOGOUT, SET_IS_DARK_MODE } from 'store/constants';
import { IRootReducerState } from 'store/store';
import {
  setBase64Metadata,
  setIntentUUID,
} from 'store/SubmitConfiguration/actions/submitConfiguration';
import {
  setCompany,
  setIntent,
  setIsLoggedIn,
  setUserInfo,
} from 'store/User/actions/user';
import {
  setAppMode,
  setIsLoadingScreenShown,
  setIsMobileApp,
  setQueryParamsProcessed,
} from 'store/Common/actions/common';
import { AppMode } from 'store/Common/reducers/common';
import i18n from 'providers/i18n/i18n';
import LocalStorageService from 'services/LocalStorageService';
import {
  LOCAL_STORAGE_ACCESS_TOKEN,
  LOCAL_STORAGE_BORROWED_SESSION,
  LOCAL_STORAGE_REFRESH_TOKEN,
} from 'services/api/constants';
import { useGetCompanySupplierPermissionsAndIntent } from './hooks';
import { IUserData } from 'store/User/types';
import { decodeTokenAndSetUserData } from 'pages/Auth/LoginPage/helpers';

interface IUrlParamsProcessorProps {
  children: React.ReactNode;
}

const EMPTY_USER_INFO: IUserData = {
  id: null,
  email: null,
  first_name: null,
  last_name: null,
  is_admin: false,
  permissions: null,
};

const removeAccessAndRefreshTokens = async () => {
  await LocalStorageService.removeItem(LOCAL_STORAGE_ACCESS_TOKEN);
  await LocalStorageService.removeItem(LOCAL_STORAGE_REFRESH_TOKEN);
};

export const UrlParamsProcessor = ({ children }: IUrlParamsProcessorProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const { queryParamsProcessed, appMode } = useSelector(
    (state: IRootReducerState) => state.commonInfo
  );

  const { mutate: getCompanySupplierPermissionsAndIntent } =
    useGetCompanySupplierPermissionsAndIntent();

  useEffect(() => {
    if (queryParamsProcessed) return;

    // Process intentUUID (is present when opening in Blueprint or Dealer Panel mode)
    const intentUUID = queryParams.get('intentUUID');
    if (intentUUID) {
      const base64Metadata = queryParams.get('base64Metadata');
      dispatch(setIntentUUID(intentUUID));
      dispatch(setBase64Metadata(base64Metadata));
    } else {
      dispatch(setIntentUUID(''));
      dispatch(setIntent(null));
      dispatch(setBase64Metadata(null));
    }

    // Process isDealerPanel
    const isDealerPanel = queryParams.get('isDealerPanel') === 'true';

    // The admin panel opens forms in a bare tab purely to build a template. It
    // wants EXTERNAL's chrome — no navigation, no side menu, just the config
    // form — but authenticates with the ordinary JWT rather than an intentUUID,
    // so it must not take the token-clearing branch below.
    const isAdminPanel = queryParams.get('isAdminPanel') === 'true';
    const accessToken = queryParams.get('accessToken');

    // Self-heal a leftover borrowed session. The token the host hands over is an
    // admin's and has no selected company, so an ordinary visit that picks it up
    // renders a blank page.
    const hasStaleBorrowedSession =
      LocalStorageService.getItem(LOCAL_STORAGE_BORROWED_SESSION) === 'true' &&
      !(isAdminPanel && accessToken);
    if (hasStaleBorrowedSession) {
      LocalStorageService.clear();
      dispatch({ type: LOGOUT });
    }

    // Process app mode

    if (isDealerPanel && intentUUID) {
      dispatch(setAppMode(AppMode.DEALER_PANEL));
    } else if (intentUUID) {
      dispatch(setAppMode(AppMode.EXTERNAL));
    } else if (isAdminPanel) {
      dispatch(setAppMode(AppMode.EXTERNAL));
    } else {
      // Preserve app mode on page refresh
      if (appMode !== AppMode.SUPPLIER) {
        dispatch(setAppMode(AppMode.BUYER)); // User will log in through normal auth flow (if not already logged in)
      }
    }

    // Presentation params are shared by every embedded entry.
    if (isDealerPanel || intentUUID || isAdminPanel) {
      // Process dark mode
      const isDarkMode = queryParams.get('isDarkMode') === 'true';
      dispatch({ type: SET_IS_DARK_MODE, payload: isDarkMode });

      // Process language
      const lang = queryParams.get('lang');
      if (lang) {
        i18n.changeLanguage(lang);
      }

      // Process isMobileApp
      const isMobileApp = queryParams.get('isMobileApp');
      if (isMobileApp) {
        dispatch(setIsMobileApp(true));
      }
    }

    // Only the intent-authenticated entries clear the local tokens — the intent
    // replaces them. The admin panel tab is authenticated by the JWT that is
    // already there, so wiping it would log the user out of their own session.
    if (isDealerPanel || intentUUID) {
      // Reset user info and company for non-authenticated modes
      removeAccessAndRefreshTokens(); // User is instead authenticated by intentUUID
      dispatch(setIsLoggedIn(false)); // User is not logged in as supplier or company
      dispatch(setUserInfo(EMPTY_USER_INFO));
      dispatch(setCompany(null));
      dispatch(setIsLoadingScreenShown(true));

      getCompanySupplierPermissionsAndIntent(undefined, {
        onSuccess: (data) => {
          dispatch(setIntent(data.intent));
          dispatch(setCompany(data.company));
          dispatch(
            setUserInfo({ ...EMPTY_USER_INFO, permissions: data.permissions })
          );
          // Loading screen will be hidden based on logic in useHandleLoadingScreenState()
        },
      });
    }

    // The admin panel runs on a different host (admin-dev vs app-dev), so it
    // cannot share localStorage — the tab arrives with no token, and the request
    // interceptor logs the user out rather than merely failing. The host hands
    // the JWT over in the URL instead.
    if (isAdminPanel && accessToken) {
      LocalStorageService.setItem(LOCAL_STORAGE_ACCESS_TOKEN, accessToken);
      // Marks the session as borrowed so it can be cleared when this tab closes.
      // Without that, the admin's token — which has no selected company — stays
      // behind and the user's next ordinary visit to forms is a blank page.
      LocalStorageService.setItem(LOCAL_STORAGE_BORROWED_SESSION, 'true');
      decodeTokenAndSetUserData(accessToken, dispatch);
      dispatch(setIsLoggedIn(true));
    }

    dispatch(setQueryParamsProcessed(true));

    if (accessToken) {
      // Drop the token from the address bar so it does not linger in history,
      // bookmarks or a Referer header. Done through the router rather than
      // history.replaceState: useNavigateWithQuery rebuilds URLs from the
      // router's own location, so a token it still knew about would be put
      // straight back on the next navigation (the BOM page, for instance).
      queryParams.delete('accessToken');
      navigate(`${location.pathname}?${queryParams.toString()}`, {
        replace: true,
      });
    }
  }, [queryParams, queryParamsProcessed]);

  return <>{children}</>;
};
